import type { AdvancedLinking, Condition, Rule } from '$lib/types/AdvancedLinking';
import type { FormValues } from '$lib/types/Form';

/**
 * Evaluates advanced linking conditions against user answers to determine navigation target
 */
export class AdvancedLinkingEvaluator {
    /**
     * Evaluates all rules for a button and returns the appropriate navigation target
     * @param blockId - The ID of the button being clicked
     * @param userAnswers - User answers from all question types (FormQuestion, MediaQuestion, etc.)
     * @param advancedLinking - Advanced linking configuration
     * @returns Navigation target (pageId or URL) or null if no rules match
     */
    static evaluate(
        blockId: string,
        userAnswers: FormValues,
        advancedLinking: AdvancedLinking,
    ): {
        target: string;
        isUrl: boolean;
        componentId?: string;
        pageId?: string;
        isNewTab?: boolean;
    } | null {
        const linkingConfig = advancedLinking[blockId];

        if (!linkingConfig || !linkingConfig.rules) {
            return null;
        }

        // Evaluate each rule in order
        for (const rule of linkingConfig.rules) {
            if (this.evaluateRule(rule, userAnswers)) {
                const isScrollAction = rule.result === 'scroll';
                const target = isScrollAction ? rule.result : rule.url || rule.result;

                return {
                    target,
                    isUrl: !!rule.url,
                    componentId: isScrollAction ? rule.componentId : undefined,
                    pageId: isScrollAction ? rule.url : undefined,
                    isNewTab: rule.isNewTab,
                };
            }
        }

        // If no rules match, use fallback
        if (linkingConfig.fallback) {
            const isScrollAction = linkingConfig.fallback === 'scroll';
            const target = isScrollAction
                ? linkingConfig.fallback
                : linkingConfig.url || linkingConfig.fallback;

            return {
                target,
                isUrl: !!linkingConfig.url,
                componentId: isScrollAction ? linkingConfig.componentId : undefined,
                pageId: isScrollAction ? linkingConfig.url : undefined,
                isNewTab: linkingConfig.isNewTab,
            };
        }

        return null;
    }

    /**
     * Evaluates a single rule against user answers
     * @param rule - The rule to evaluate
     * @param userAnswers - User answers from all question types
     * @returns true if the rule conditions are met
     */
    private static evaluateRule(rule: Rule, userAnswers: FormValues): boolean {
        if (!rule.conditions || rule.conditions.length === 0) {
            return false;
        }

        const results = rule.conditions.map((condition) =>
            this.evaluateCondition(condition, userAnswers),
        );

        // Process conditions based on operators
        return this.processConditionResults(results, rule.conditions);
    }

    /**
     * Processes condition results based on operators (AND, OR, WHEN)
     * @param results - Array of boolean results from condition evaluations
     * @param conditions - Array of conditions with their operators
     * @returns Combined result
     */
    private static processConditionResults(results: boolean[], conditions: Condition[]): boolean {
        if (results.length === 0) {
            return false;
        }

        if (results.length === 1) {
            return results[0];
        }

        // Process OR and AND operators
        let result = results[0];
        for (let i = 1; i < results.length; i++) {
            const operator = conditions[i].operator;

            switch (operator) {
                case 'OR':
                    result = result || results[i];
                    break;
                case 'AND':
                case 'WHEN': // WHEN acts like AND in this context
                    result = result && results[i];
                    break;
                default:
                    // For unknown operators, default to AND behavior
                    result = result && results[i];
            }
        }

        return result;
    }

    /**
     * Evaluates contains/not-contains logic with support for comma-separated values
     * @param fieldValue - The field value from user answers
     * @param expectedValue - The expected value to compare against
     * @param negate - Whether to negate the result (true for NOT_CONTAINS)
     * @returns true if the condition is met
     */
    private static evaluateContains(
        fieldValue: unknown,
        expectedValue: unknown,
        negate: boolean,
    ): boolean {
        const expectedStr = String(expectedValue);
        const fieldStr = String(fieldValue || '');

        if (expectedStr.includes(',')) {
            const fieldValues = fieldStr.split(',').map((value) => value.trim().toLowerCase());
            const expectedValues = expectedStr
                .split(',')
                .map((value) => value.trim().toLowerCase());

            if (negate) {
                // NOT_CONTAINS: return true if NONE of the field values match any expected value
                return !fieldValues.some((fieldVal) => expectedValues.includes(fieldVal));
            } else {
                // CONTAINS: return true if ALL expected values are in field values
                return expectedValues.every((expectedVal) => fieldValues.includes(expectedVal));
            }
        } else {
            const result = fieldStr.toLowerCase().includes(expectedStr.toLowerCase());
            return negate ? !result : result;
        }
    }

    /**
     * Evaluates equality with order-insensitive set comparison for comma-separated values.
     *
     * Multi-value intent is signalled by a comma in the rule's configured value (mirroring
     * `evaluateContains`), not by a comma in the user's answer — otherwise a free-text answer
     * like "Smith, John" would be silently tokenised when the rule author meant a literal string.
     */
    private static evaluateEquals(
        fieldValue: unknown,
        expectedValue: unknown,
        negate: boolean,
    ): boolean {
        const expectedStr = String(expectedValue);

        // Checkbox answers are booleans while the rule stores the string 'true'/'false',
        // so a strict comparison would never match.
        if (
            typeof fieldValue === 'boolean' &&
            (expectedStr === 'true' || expectedStr === 'false')
        ) {
            const result = fieldValue === (expectedStr === 'true');

            return negate ? !result : result;
        }

        if (expectedStr.includes(',')) {
            const fieldStr = String(fieldValue || '');
            const fieldValues = fieldStr.split(',').map((value) => value.trim().toLowerCase());
            const expectedValues = expectedStr
                .split(',')
                .map((value) => value.trim().toLowerCase());

            const result =
                fieldValues.length === expectedValues.length &&
                expectedValues.every((expectedVal) => fieldValues.includes(expectedVal));

            return negate ? !result : result;
        }

        const result = fieldValue === expectedValue;

        return negate ? !result : result;
    }

    /**
     * Evaluates a single condition against user answers
     * @param condition - The condition to evaluate
     * @param userAnswers - User answers from all question types
     * @returns true if the condition is met
     */
    private static evaluateCondition(condition: Condition, userAnswers: FormValues): boolean {
        const fieldValue = userAnswers[condition.selectedOption];
        const expectedValue = condition.value;

        switch (condition.comparator) {
            case 'CONTAINS':
                return this.evaluateContains(fieldValue, expectedValue, false);

            case 'NOT_CONTAINS':
                return this.evaluateContains(fieldValue, expectedValue, true);

            case 'EQUALS':
                return this.evaluateEquals(fieldValue, expectedValue, false);

            case 'NOT_EQUALS':
                return this.evaluateEquals(fieldValue, expectedValue, true);

            // HAS_VALUE is the legacy name of WAS_ANSWERED
            case 'WAS_ANSWERED':
            case 'HAS_VALUE':
                return fieldValue !== null && fieldValue !== undefined && fieldValue !== '';

            // HAS_NO_VALUE is the legacy name of WAS_NOT_ANSWERED
            case 'WAS_NOT_ANSWERED':
            case 'HAS_NO_VALUE':
                return fieldValue === null || fieldValue === undefined || fieldValue === '';

            case 'IS_EXACTLY_ON':
                return this.evaluateDateComparison(fieldValue, expectedValue, 'exact');

            case 'IS_BEFORE':
                return this.evaluateDateComparison(fieldValue, expectedValue, 'before');

            case 'IS_AFTER':
                return this.evaluateDateComparison(fieldValue, expectedValue, 'after');

            case 'IS_CHECKED':
                return Boolean(fieldValue);

            case 'HAS_FILE':
                return Boolean(fieldValue) && fieldValue !== 'failed-upload';

            // HAS_PAID is the legacy name of IS_PAID
            case 'IS_PAID':
            case 'HAS_PAID':
                return fieldValue === true;

            // HAS_BOOKED is the legacy name of IS_BOOKED
            case 'IS_BOOKED':
            case 'HAS_BOOKED':
                return fieldValue === true;

            default:
                console.warn(`Unknown comparator: ${condition.comparator}`);
                return false;
        }
    }

    /**
     * Evaluates date comparison conditions with ISO timestamps
     *
     * Comparison Behavior:
     * - IS_EXACTLY_ON: Compares date components only (year, month, day), ignoring time
     * - IS_BEFORE: Compares full timestamps (date + time)
     * - IS_AFTER: Compares full timestamps (date + time)
     *
     * @example
     * // IS_EXACTLY_ON - Date components only
     * fieldValue: '2024-12-25T14:30:00.000Z'
     * expectedValue: '2024-12-25T09:00:00.000Z'
     * Result: true (same date, different times)
     *
     * @example
     * // IS_BEFORE/IS_AFTER - Full timestamp comparison
     * fieldValue: '2024-12-25T14:30:00.000Z'
     * expectedValue: '2024-12-25T09:00:00.000Z'
     * Result: IS_AFTER returns true (14:30 is after 09:00)
     *
     * @param fieldValue - The ISO timestamp from user answers
     * @param expectedValue - The ISO timestamp to compare against
     * @param comparison - The type of comparison: 'exact', 'before', or 'after'
     * @returns true if the date condition is met
     */
    private static evaluateDateComparison(
        fieldValue: unknown,
        expectedValue: unknown,
        comparison: 'exact' | 'before' | 'after',
    ): boolean {
        if (!fieldValue || !expectedValue) return false;

        const fieldDate = new Date(String(fieldValue));
        const expectedDate = new Date(String(expectedValue));

        // Check if dates are valid
        if (isNaN(fieldDate.getTime()) || isNaN(expectedDate.getTime())) {
            return false;
        }

        const fieldTime = fieldDate.getTime();
        const expectedTime = expectedDate.getTime();

        switch (comparison) {
            case 'exact':
                // Compare date components only (year, month, day) - ignore time
                return (
                    fieldDate.getUTCFullYear() === expectedDate.getUTCFullYear() &&
                    fieldDate.getUTCMonth() === expectedDate.getUTCMonth() &&
                    fieldDate.getUTCDate() === expectedDate.getUTCDate()
                );
            case 'before':
                // Compare full timestamps (includes time component)
                return fieldTime < expectedTime;
            case 'after':
                // Compare full timestamps (includes time component)
                return fieldTime > expectedTime;
            default:
                return false;
        }
    }
}
